import React, { useState, useMemo, useCallback, useRef } from 'react';
import { View, Text, Pressable, ScrollView, Dimensions } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import { userNameAtom } from '../store/company';
import XLSX from 'xlsx';
import { Line } from 'react-chartjs-2';
import { SERIES, alpha } from '../styles/chartColors';
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import moment from 'moment';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend);

const ALLOWED_NAMES = ['matt', 'tanner', 'weston', 'wayne', 'sammy', 'steve'];

const Backlog = () => {
    const [userName] = useAtom(userNameAtom);
    const [data, setData] = useState([]);
    const [distribution, setDistribution] = useState('even'); // 'even' or 'bell'
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'chart'
    const fileInputRef = useRef(null);

    const firstName = (userName || '').split(' ')[0].toLowerCase();
    const hasAccess = process.env.NODE_ENV !== 'production' || ALLOWED_NAMES.includes(firstName);

    if (!hasAccess) {
        return (
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
                <Text style={{ fontSize: 20, color: '#d32f2f' }}>Access Denied</Text>
                <Text style={{ fontSize: 14, color: '#666', marginTop: 8 }}>You do not have permission to view this page.</Text>
            </View>
        );
    }

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            const bstr = evt.target.result;
            const wb = XLSX.read(bstr, { type: 'binary' });
            const wsname = wb.SheetNames[0];
            const ws = wb.Sheets[wsname];
            const rawData = XLSX.utils.sheet_to_json(ws, { defval: '' });
            const parsed = rawData.map((row, idx) => ({
                id: idx,
                task_code: row.task_code || row['Activity ID'] || '',
                status_code: row.status_code || row['Activity Status'] || '',
                wbs_id: row.wbs_id || row['WBS Code'] || '',
                user_field_150: row.user_field_150 || row['Activity UDF Text 3'] || '',
                user_field_148: row.user_field_148 || row['Activity UDF Text 1'] || '',
                user_field_149: row.user_field_149 || row['Activity UDF Text 2'] || '',
                task_name: row.task_name || row['Activity Name'] || '',
                start_date: parseExcelDate(row.start_date || row['(*)Start'] || ''),
                end_date: parseExcelDate(row.end_date || row['(*)Finish'] || ''),
                target_drtn_hr_cnt: parseFloat(row.target_drtn_hr_cnt || row['Original Duration(d)'] || 0),
                target_work_cost: parseFloat(row.target_work_cost || row['(*)Budgeted Labor Cost($)'] || 0),
                resource: row.resource || row['(*)Resource Primary Constraint'] || '',
                cstr_type: row.cstr_type || row['Primary Constraint'] || '',
                cstr_date: parseExcelDate(row.cstr_date || row['Primary Constraint Date'] || ''),
                delete_record_flag: row.delete_record_flag || row['Delete This Row'] || '',
            }));
            setData(parsed.filter(r => r.task_code));
        };
        reader.readAsBinaryString(file);
        e.target.value = '';
    };

    const parseExcelDate = (val) => {
        if (!val) return '';
        if (typeof val === 'number') {
            const d = XLSX.SSF.parse_date_code(val);
            return moment({ year: d.y, month: d.m - 1, day: d.d }).format('YYYY-MM-DD');
        }
        const m = moment(val, ['M/D/YYYY HH:mm', 'YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY'], true);
        return m.isValid() ? m.format('YYYY-MM-DD') : val;
    };

    const getMonthsBetween = (start, end) => {
        const s = moment(start, 'YYYY-MM-DD');
        const e = moment(end, 'YYYY-MM-DD');
        if (!s.isValid() || !e.isValid() || e.isBefore(s)) return [];
        const months = [];
        const cur = s.clone().startOf('month');
        while (cur.isSameOrBefore(e, 'month')) {
            months.push(cur.format('YYYY-MM'));
            cur.add(1, 'month');
        }
        return months;
    };

    const bellWeights = (n) => {
        if (n <= 1) return [1];
        const weights = [];
        const mid = (n - 1) / 2;
        const sigma = n / 6;
        for (let i = 0; i < n; i++) {
            weights.push(Math.exp(-0.5 * Math.pow((i - mid) / sigma, 2)));
        }
        const sum = weights.reduce((a, b) => a + b, 0);
        return weights.map(w => w / sum);
    };

    const distributeRevenue = (amount, months, type) => {
        if (!months.length) return {};
        if (type === 'bell') {
            const weights = bellWeights(months.length);
            const result = {};
            months.forEach((m, i) => {
                result[m] = Math.round(amount * weights[i] * 100) / 100;
            });
            return result;
        }
        // even
        const perMonth = Math.round((amount / months.length) * 100) / 100;
        const result = {};
        months.forEach((m, i) => {
            result[m] = i === months.length - 1 ? Math.round((amount - perMonth * (months.length - 1)) * 100) / 100 : perMonth;
        });
        return result;
    };

    const distributedData = useMemo(() => {
        return data.map(row => {
            const months = getMonthsBetween(row.start_date, row.end_date);
            const revenue = row.target_work_cost || 0;
            const dist = distributeRevenue(revenue, months, distribution);
            return { ...row, monthlyRevenue: dist, months };
        });
    }, [data, distribution]);

    const allMonths = useMemo(() => {
        const set = new Set();
        distributedData.forEach(row => {
            Object.keys(row.monthlyRevenue).forEach(m => set.add(m));
        });
        return [...set].sort();
    }, [distributedData]);

    const monthlyTotals = useMemo(() => {
        const totals = {};
        allMonths.forEach(m => { totals[m] = 0; });
        distributedData.forEach(row => {
            Object.entries(row.monthlyRevenue).forEach(([m, v]) => {
                totals[m] = (totals[m] || 0) + v;
            });
        });
        return totals;
    }, [distributedData, allMonths]);

    const handleExport = () => {
        const exportRows = distributedData.map(row => {
            const base = {
                'Activity ID': row.task_code,
                'Status': row.status_code,
                'WBS Code': row.wbs_id,
                'Client': row.user_field_150,
                'UDF Text 1': row.user_field_148,
                'UDF Text 2': row.user_field_149,
                'Task Name': row.task_name,
                'Start': row.start_date,
                'End': row.end_date,
                'Duration (d)': row.target_drtn_hr_cnt,
                'Total Revenue': row.target_work_cost,
                'Resource': row.resource,
            };
            allMonths.forEach(m => {
                base[m] = row.monthlyRevenue[m] || 0;
            });
            return base;
        });
        // Add totals row
        const totalsRow = { 'Activity ID': 'TOTAL', 'Status': '', 'WBS Code': '', 'Client': '', 'UDF Text 1': '', 'UDF Text 2': '', 'Task Name': '', 'Start': '', 'End': '', 'Duration (d)': '', 'Total Revenue': distributedData.reduce((s, r) => s + (r.target_work_cost || 0), 0), 'Resource': '' };
        allMonths.forEach(m => { totalsRow[m] = monthlyTotals[m] || 0; });
        exportRows.push(totalsRow);

        const ws = XLSX.utils.json_to_sheet(exportRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Backlog Revenue');
        XLSX.writeFile(wb, `Backlog_Revenue_${distribution}_${moment().format('YYYY-MM-DD')}.xlsx`);
    };

    const chartData = useMemo(() => ({
        labels: allMonths,
        datasets: [
            {
                label: 'Monthly Revenue',
                data: allMonths.map(m => monthlyTotals[m] || 0),
                borderColor: SERIES[0],
                backgroundColor: alpha(SERIES[0], 0.1),
                fill: true,
                tension: 0.3,
                pointRadius: 3,
            },
            {
                label: 'Cumulative Revenue',
                data: allMonths.reduce((acc, m) => {
                    const prev = acc.length ? acc[acc.length - 1] : 0;
                    acc.push(prev + (monthlyTotals[m] || 0));
                    return acc;
                }, []),
                borderColor: SERIES[1],
                backgroundColor: alpha(SERIES[1], 0.1),
                fill: false,
                tension: 0.3,
                pointRadius: 3,
                borderDash: [5, 5],
                yAxisID: 'y1',
            }
        ]
    }), [allMonths, monthlyTotals]);

    const chartOptions = {
        responsive: true,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { position: 'top' },
            title: { display: true, text: `Backlog Revenue Distribution (${distribution === 'even' ? 'Even' : 'Bell Curve'})` },
            tooltip: {
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: $${ctx.parsed.y.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                }
            }
        },
        scales: {
            y: {
                type: 'linear',
                position: 'left',
                title: { display: true, text: 'Monthly Revenue ($)' },
                ticks: { callback: v => '$' + v.toLocaleString() }
            },
            y1: {
                type: 'linear',
                position: 'right',
                title: { display: true, text: 'Cumulative Revenue ($)' },
                ticks: { callback: v => '$' + v.toLocaleString() },
                grid: { drawOnChartArea: false },
            }
        }
    };

    const fCurrency = (v) => {
        if (v == null || isNaN(v)) return '$0';
        return '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    };

    return (
        <ScrollView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
            <View style={{ padding: 20 }}>
                {/* Header */}
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <Text style={{ fontSize: 24, fontWeight: 'bold', color: '#1e293b' }}>Backlog</Text>
                    <View style={{ flexDirection: 'row', gap: 10 }}>
                        <Pressable
                            onPress={() => fileInputRef.current?.click()}
                            style={{ backgroundColor: '#2563eb', padding: '8px 16px', borderRadius: 6 }}
                        >
                            <Text style={{ color: '#fff', fontWeight: '600', fontSize: 13 }}>Import Excel</Text>
                        </Pressable>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleFileUpload}
                            style={{ display: 'none' }}
                        />
                        {data.length > 0 && (
                            <Pressable
                                onPress={handleExport}
                                style={{ backgroundColor: '#16a34a', padding: '8px 16px', borderRadius: 6 }}
                            >
                                <Text style={{ color: '#fff', fontWeight: '600', fontSize: 13 }}>Export with Revenue</Text>
                            </Pressable>
                        )}
                    </View>
                </View>

                {/* Controls */}
                {data.length > 0 && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                            <Text style={{ fontSize: 13, fontWeight: '600', color: '#475569' }}>Distribution:</Text>
                            <Pressable
                                onPress={() => setDistribution('even')}
                                style={{
                                    padding: '6px 14px', borderRadius: 6,
                                    backgroundColor: distribution === 'even' ? '#2563eb' : '#e2e8f0',
                                }}
                            >
                                <Text style={{ color: distribution === 'even' ? '#fff' : '#334155', fontWeight: '600', fontSize: 13 }}>Even</Text>
                            </Pressable>
                            <Pressable
                                onPress={() => setDistribution('bell')}
                                style={{
                                    padding: '6px 14px', borderRadius: 6,
                                    backgroundColor: distribution === 'bell' ? '#2563eb' : '#e2e8f0',
                                }}
                            >
                                <Text style={{ color: distribution === 'bell' ? '#fff' : '#334155', fontWeight: '600', fontSize: 13 }}>Bell Curve</Text>
                            </Pressable>
                        </View>

                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                            <Text style={{ fontSize: 13, fontWeight: '600', color: '#475569' }}>View:</Text>
                            <Pressable
                                onPress={() => setViewMode('table')}
                                style={{
                                    padding: '6px 14px', borderRadius: 6,
                                    backgroundColor: viewMode === 'table' ? '#2563eb' : '#e2e8f0',
                                }}
                            >
                                <Text style={{ color: viewMode === 'table' ? '#fff' : '#334155', fontWeight: '600', fontSize: 13 }}>Table</Text>
                            </Pressable>
                            <Pressable
                                onPress={() => setViewMode('chart')}
                                style={{
                                    padding: '6px 14px', borderRadius: 6,
                                    backgroundColor: viewMode === 'chart' ? '#2563eb' : '#e2e8f0',
                                }}
                            >
                                <Text style={{ color: viewMode === 'chart' ? '#fff' : '#334155', fontWeight: '600', fontSize: 13 }}>Chart</Text>
                            </Pressable>
                        </View>

                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                            <Text style={{ fontSize: 13, color: '#64748b' }}>
                                {data.length} activities | Total Revenue: {fCurrency(data.reduce((s, r) => s + (r.target_work_cost || 0), 0))}
                            </Text>
                        </View>
                    </View>
                )}

                {/* Chart View */}
                {data.length > 0 && viewMode === 'chart' && (
                    <View style={{ backgroundColor: '#fff', borderRadius: 8, padding: 20, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                        <Line data={chartData} options={chartOptions} />
                    </View>
                )}

                {/* Table View */}
                {data.length > 0 && viewMode === 'table' && (
                    <View style={{ backgroundColor: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                        <th style={thStyle}>Activity ID</th>
                                        <th style={thStyle}>Task Name</th>
                                        <th style={thStyle}>Client</th>
                                        <th style={thStyle}>Start</th>
                                        <th style={thStyle}>End</th>
                                        <th style={{ ...thStyle, textAlign: 'right' }}>Total Revenue</th>
                                        {allMonths.map(m => (
                                            <th key={m} style={{ ...thStyle, textAlign: 'right', minWidth: 80 }}>{m}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {distributedData.map((row, i) => (
                                        <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                            <td style={tdStyle}>{row.task_code}</td>
                                            <td style={{ ...tdStyle, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.task_name}</td>
                                            <td style={tdStyle}>{row.user_field_150}</td>
                                            <td style={tdStyle}>{row.start_date}</td>
                                            <td style={tdStyle}>{row.end_date}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>{fCurrency(row.target_work_cost)}</td>
                                            {allMonths.map(m => (
                                                <td key={m} style={{ ...tdStyle, textAlign: 'right' }}>
                                                    {row.monthlyRevenue[m] ? fCurrency(row.monthlyRevenue[m]) : ''}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    {/* Totals row */}
                                    <tr style={{ borderTop: '2px solid #cbd5e1', backgroundColor: '#f1f5f9', fontWeight: 'bold' }}>
                                        <td style={tdStyle} colSpan={5}>TOTAL</td>
                                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>
                                            {fCurrency(distributedData.reduce((s, r) => s + (r.target_work_cost || 0), 0))}
                                        </td>
                                        {allMonths.map(m => (
                                            <td key={m} style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>
                                                {fCurrency(monthlyTotals[m])}
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </View>
                )}

                {/* Empty state */}
                {data.length === 0 && (
                    <View style={{ backgroundColor: '#fff', borderRadius: 8, padding: 60, alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                        <Text style={{ fontSize: 16, color: '#94a3b8', marginBottom: 8 }}>No data loaded</Text>
                        <Text style={{ fontSize: 13, color: '#cbd5e1' }}>Import an Excel file with backlog activities to get started.</Text>
                    </View>
                )}
            </View>
        </ScrollView>
    );
};

const thStyle = {
    padding: '8px 10px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#475569',
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    backgroundColor: '#f8fafc',
};

const tdStyle = {
    padding: '6px 10px',
    color: '#334155',
    whiteSpace: 'nowrap',
};

export default Backlog;
